import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Users, Heart, CalendarDays, Search, Check, X, Download } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const PRESETS = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
];

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const shortDate = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return iso; } };

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (filename, rows) => {
  const content = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const Metric = ({ label, value, sub, tone = 'text-navy' }) => (
  <div className="bg-white rounded-2xl p-5 card-soft" data-testid={`mi-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</p>
    <p className={`font-heading text-3xl lg:text-4xl mt-1 font-light ${tone}`}>{value ?? '—'}</p>
    {sub && <p className="text-xs text-navy/50 mt-1">{sub}</p>}
  </div>
);

export default function AdminMemberInsights() {
  const { user } = useAuth();
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(todayIso());
  const [preset, setPreset] = useState('30');
  const [allMembers, setAllMembers] = useState([]);
  const [selected, setSelected] = useState([]); // [] = all
  const [pickerOpen, setPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyPreset = (p) => {
    setPreset(p.id);
    setStart(isoDaysAgo(p.days));
    setEnd(todayIso());
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ start, end });
    if (selected.length) params.set('member_ids', selected.join(','));
    http.get(`/admin/member-insights?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load insights.'))
      .finally(() => setLoading(false));
  }, [start, end, selected]);

  useEffect(() => {
    if (!user?.is_admin) return;
    http.get('/admin/member-insights/members').then((r) => setAllMembers(r.data.members || [])).catch(() => {});
  }, [user]);

  useEffect(() => { if (user?.is_admin) load(); }, [load, user]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter((m) => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [allMembers, memberSearch]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/app" replace />;

  const toggleMember = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const w = data?.wellness || {};
  const c = data?.checkins || {};
  const conv = data?.conversions || {};
  const wsummary = w.summary || {};
  const changeTone = (wsummary.change || 0) > 0 ? 'text-green-600' : (wsummary.change || 0) < 0 ? 'text-red-600' : 'text-navy';
  const selLabel = selected.length === 0 ? 'All members' : `${selected.length} selected`;

  const exportDailyCsv = () => {
    const rateByDate = {};
    (c.trend || []).forEach((t) => { rateByDate[t.date] = t; });
    const rows = [['Date', 'Avg wellness', 'Check-in rate (%)', 'Check-ins']];
    (w.series || []).forEach((s) => {
      const t = rateByDate[s.date] || {};
      rows.push([s.date, s.score ?? '', t.rate ?? '', t.count ?? '']);
    });
    downloadCsv(`member-insights_${data?.range?.start}_to_${data?.range?.end}.csv`, rows);
  };
  const exportMembersCsv = () => {
    const rows = [['Member', 'Days checked in', 'Check-in rate (%)']];
    (c.member_rows || []).forEach((m) => rows.push([m.name, m.days_checked, m.rate]));
    downloadCsv(`member-checkin-rates_${data?.range?.start}_to_${data?.range?.end}.csv`, rows);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-16" data-testid="admin-member-insights">
      <div className="max-w-6xl mx-auto px-4 lg:px-6 pt-6">
        <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="mi-back">
          <ArrowLeft size={16} /> Back to admin
        </Link>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl">Member Insights</h1>
            <p className="text-navy/55 text-sm mt-1">Wellness score progress & daily check-in rate over time.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportDailyCsv} disabled={!data || loading} data-testid="mi-export-daily" className="inline-flex items-center gap-2 text-sm bg-white border border-navy/10 rounded-full px-4 py-2 hover:bg-beige transition-smooth disabled:opacity-40">
              <Download size={14} /> Daily CSV
            </button>
            <button onClick={exportMembersCsv} disabled={!data || loading} data-testid="mi-export-members" className="inline-flex items-center gap-2 text-sm bg-white border border-navy/10 rounded-full px-4 py-2 hover:bg-beige transition-smooth disabled:opacity-40">
              <Download size={14} /> Members CSV
            </button>
            <button onClick={load} data-testid="mi-refresh" className="inline-flex items-center gap-2 text-sm bg-white border border-navy/10 rounded-full px-4 py-2 hover:bg-beige transition-smooth">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-5 bg-white rounded-2xl p-4 card-soft flex flex-col lg:flex-row lg:items-end gap-4" data-testid="mi-controls">
          <div>
            <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-1.5">Date range</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PRESETS.map((p) => (
                <button key={p.id} onClick={() => applyPreset(p)} data-testid={`mi-preset-${p.id}`}
                  className={`text-xs rounded-full px-3 py-1.5 transition-smooth ${preset === p.id ? 'bg-navy text-white' : 'bg-beige text-navy/70 hover:brightness-95'}`}>
                  {p.label}
                </button>
              ))}
              <span className="inline-flex items-center gap-1.5 ml-1">
                <CalendarDays size={14} className="text-navy/40" />
                <input type="date" value={start} max={end} onChange={(e) => { setStart(e.target.value); setPreset('custom'); }} data-testid="mi-start"
                  className="text-xs border border-navy/10 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-gold" />
                <span className="text-navy/40 text-xs">to</span>
                <input type="date" value={end} min={start} max={todayIso()} onChange={(e) => { setEnd(e.target.value); setPreset('custom'); }} data-testid="mi-end"
                  className="text-xs border border-navy/10 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-gold" />
              </span>
            </div>
          </div>

          <div className="relative">
            <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-1.5">Members</p>
            <button onClick={() => setPickerOpen((o) => !o)} data-testid="mi-member-toggle"
              className="inline-flex items-center gap-2 text-sm bg-beige rounded-full px-4 py-2 hover:brightness-95 transition-smooth">
              <Users size={14} /> {selLabel}
            </button>
            {pickerOpen && (
              <div className="absolute z-20 mt-2 w-80 max-w-[85vw] bg-white rounded-2xl card-soft border border-navy/10 p-3" data-testid="mi-member-picker">
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => setSelected([])} className="text-xs text-navy/60 hover:text-navy" data-testid="mi-select-all">Select all</button>
                  <button onClick={() => setPickerOpen(false)} className="text-navy/40 hover:text-navy"><X size={15} /></button>
                </div>
                <div className="flex items-center gap-2 bg-beige rounded-lg px-2.5 py-1.5 mb-2">
                  <Search size={13} className="text-navy/40" />
                  <input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search members…" data-testid="mi-member-search"
                    className="flex-1 bg-transparent text-xs focus:outline-none" />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-0.5">
                  {filteredMembers.map((m) => {
                    const on = selected.includes(m.id);
                    return (
                      <button key={m.id} onClick={() => toggleMember(m.id)} data-testid={`mi-member-${m.id}`}
                        className={`w-full flex items-center gap-2 text-left rounded-lg px-2 py-1.5 text-xs transition-smooth ${on ? 'bg-navy text-white' : 'hover:bg-beige'}`}>
                        <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? 'bg-gold border-gold' : 'border-navy/20'}`}>{on && <Check size={11} className="text-navy" />}</span>
                        <span className="flex-1 min-w-0"><span className="block truncate font-medium">{m.name}</span><span className="block truncate opacity-60">{m.email}</span></span>
                      </button>
                    );
                  })}
                  {filteredMembers.length === 0 && <p className="text-xs text-navy/40 text-center py-4">No members found.</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Metric label="Avg wellness" value={wsummary.avg != null ? wsummary.avg : '—'} sub="0–100 across range" />
          <Metric label="Wellness change" value={(wsummary.change > 0 ? '+' : '') + (wsummary.change ?? 0)} sub="first → latest" tone={changeTone} />
          <Metric label="Check-in rate" value={`${c.overall_rate ?? 0}%`} sub="days checked-in ÷ days" />
          <Metric label="Trial → paid" value={conv.rate != null ? `${conv.rate}%` : '—'} sub={`${conv.converted ?? 0} of ${conv.trials_started ?? 0} trials`} tone="text-gold" />
          <Metric label="Members" value={data?.members_count ?? '—'} sub={`${data?.range?.days ?? 0} days`} />
        </div>

        {/* Wellness chart */}
        <div className="mt-5 bg-white rounded-2xl p-5 card-soft" data-testid="mi-wellness-chart">
          <div className="flex items-center gap-2 mb-3"><Heart size={16} className="text-gold" /><h2 className="font-heading text-lg">Wellness score over time</h2></div>
          {loading ? <div className="h-64 rounded-xl bg-beige/60 animate-pulse" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={w.series || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E1D4" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#6b7280' }} minTickGap={24} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip labelFormatter={shortDate} formatter={(v) => [v == null ? 'No data' : v, 'Wellness']} />
                <Line type="monotone" dataKey="score" stroke="#0B192C" strokeWidth={2.5} dot={{ r: 2.5, fill: '#C5A059' }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
          <p className="text-[11px] text-navy/45 mt-2">{wsummary.assessments || 0} assessments in range · average across selected members per day.</p>
        </div>

        {/* Check-in rate trend */}
        <div className="mt-5 bg-white rounded-2xl p-5 card-soft" data-testid="mi-checkin-chart">
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={16} className="text-gold" /><h2 className="font-heading text-lg">Daily check-in rate</h2></div>
          {loading ? <div className="h-56 rounded-xl bg-beige/60 animate-pulse" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={c.trend || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="miFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C5A059" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C5A059" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E1D4" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#6b7280' }} minTickGap={24} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip labelFormatter={shortDate} formatter={(v) => [`${v}%`, 'Checked in']} />
                <Area type="monotone" dataKey="rate" stroke="#C5A059" strokeWidth={2} fill="url(#miFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-member table */}
        <div className="mt-5 bg-white rounded-2xl p-5 card-soft" data-testid="mi-member-table">
          <h2 className="font-heading text-lg mb-3">Check-in rate by member</h2>
          <div className="space-y-2">
            {(c.member_rows || []).map((m) => (
              <div key={m.id} className="flex items-center gap-3" data-testid={`mi-row-${m.id}`}>
                <span className="text-xs w-44 truncate text-navy/75">{m.name}</span>
                <div className="flex-1 h-2.5 bg-beige rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${Math.max(2, m.rate)}%` }} />
                </div>
                <span className="text-xs text-navy/60 w-24 text-right tabular-nums">{m.rate}% · {m.days_checked}d</span>
              </div>
            ))}
            {(!c.member_rows || c.member_rows.length === 0) && !loading && (
              <p className="text-sm text-navy/45 text-center py-6">No member data for this range.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
